/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import { ResourceReportPackage } from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { TableWrapper } from "~/components/Tables";
import { isEmpty } from "~/tools";

import { columns } from "./ResourceDetailsActionDetailsSidebarTableColumns";
import { useGetFilteredActionDetails } from "./useGetFilteredActionDetails";

type Props = {
  eventId: string;
  eventActionId: string;
  resourceId: string;
};

export function ResourceDetailsActionDetailsSidebarTable({
  eventId,
  eventActionId,
  resourceId,
}: Props) {
  const { filteredActionDetails, loading } = useGetFilteredActionDetails({
    eventId,
    eventActionId,
    resourceId,
  });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const report = [
    ...(filteredActionDetails?.installed ?? []),
    ...(filteredActionDetails?.updated ?? []),
    ...(filteredActionDetails?.available ?? []),
    ...(filteredActionDetails?.removed ?? []),
    ...(filteredActionDetails?.failed ?? []),
  ];

  return (
    <LoaderWrap loading={loading} inContent>
      <If condition={isEmpty(report)}>
        <Then>
          <NoData
            hasIcon
            message={
              "No changes were made during this action. No installations or updates were available."
            }
          />
        </Then>
        <Else>
          <TableWrapper<Partial<ResourceReportPackage>>
            loading={loading}
            data={report as ResourceReportPackage[]}
            columns={columns()}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            stripped
          />
        </Else>
      </If>
    </LoaderWrap>
  );
}
